import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./App.css";
import { Routes, Route, Navigate, Outlet } from "react-router-dom";
import { verifyRefreshToken } from "./redux/auth/authActions";
import Login from "./pages/Login";
import Home from "./pages/Home";
import Messages from "./pages/Messages";
import Notifications from "./pages/Notifications";
import WeekSchedule from "./pages/WeekSchedule";
import Navbar from "./components/Navbar";
import Loader from "./components/Loader";
import Toast from "./components/Toast";
import { useSubscription, gql } from "@apollo/client";
import moment from "moment";
import ResetPassword from "./pages/ResetPassword";
import CourseMessages from "./pages/CourseMessages";
import CourseSections from "./pages/CourseSections";
import CreateMessage from "./pages/CreateMessage";
import { fetchCourses, setMessages } from "./redux/pwaData/pwaDataActions";

const GET_NOTIFICATIONS = gql`
  subscription ($courses: [String!], $role: String!, $id: String!) {
    notifications(courses: $courses, role: $role, id: $id) {
      id
      message
      created_at
      notificationType
    }
  }
`;

const GET_MESSAGES = gql`
  subscription ($courses: [String!], $role: String!, $id: String!) {
    messages(courses: $courses, role: $role, id: $id) {
      id
      message_content
      created_at
      course
      classes
      faculty
    }
  }
`;

function RequireAuth({ user }) {
  if (!user) {
    return <Navigate to="/login" />;
  }

  return <Outlet />;
}

function App() {
  const courses = useSelector((state) => state.auth?.user?.courses);
  const role = useSelector((state) => state.auth?.user?.role);
  const id = useSelector((state) => state.auth?.user?.id);

  const last_visited = useSelector((state) => state.auth?.user?.last_visited);

  const { data, loading } = useSubscription(GET_NOTIFICATIONS, {
    variables: { courses: JSON.stringify(courses), role, id },
  });

  const { data: messagesData, loading: messagesLoading } = useSubscription(
    GET_MESSAGES,
    {
      variables: { courses: JSON.stringify(courses), role, id },
    }
  );
  const [notifications, setNotifications] = useState([]);

  const [isBadgeActive, setIsBadgeActive] = useState(false);
  const [newMessages, setNewMessages] = useState([]);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [unReadNotifications, setUnReadNotifications] = useState(0);

  const user = useSelector((state) => state.auth?.user);
  const toastMsg = useSelector((state) => state.auth?.toastMsg);
  const isLoading = useSelector((state) => state.auth?.isLoading);
  const messages = useSelector((state) => state.pwaData?.messages);
  const dispatch = useDispatch();

  const refreshToken = () => {
    dispatch(verifyRefreshToken());

    setTimeout(() => {
      refreshToken();
    }, 600000 - 1000); //10 minutes - 1 second
  };

  useEffect(() => {
    window.addEventListener("beforeinstallprompt", (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later.
      setDeferredPrompt(e);
      // Update UI notify the user they can install the PWA
      // showInstallPromotion();
    });
    refreshToken();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (JSON.stringify(data?.notifications) !== JSON.stringify(notifications)) {
      if (!loading) {
        let newNotificationsArray = [];
        if (last_visited) {
          const tempArray = [...data?.notifications, ...notifications];
          newNotificationsArray = tempArray.filter(
            (item) =>
              moment(item.created_at).format() > moment(last_visited).format()
          );
        }
        if (notifications.length > 0) {
          data && setNotifications([...data?.notifications, ...notifications]);
          data &&
            setIsBadgeActive(newNotificationsArray.length > 0 ? true : false);

          data && setUnReadNotifications(newNotificationsArray.length);
        } else {
          if (newNotificationsArray?.length > 0) {
            setIsBadgeActive(true);
            setUnReadNotifications(newNotificationsArray.length);
          }
          setNotifications(data?.notifications);
        }
      }
    }

    // eslint-disable-next-line
  }, [data]);

  useEffect(() => {
    if (JSON.stringify(messagesData?.messages) !== JSON.stringify(messages)) {
      if (!messagesLoading) {
        let newMessagesArray = [];
        if (courses) {
          const tempArray = [...messagesData?.messages, ...messages];
          newMessagesArray = tempArray.filter((item) =>
            courses.some(
              (course) =>
                item.classes.includes(course.class_id) &&
                course.course_id === item.course &&
                course.faculty_id === item.faculty &&
                moment(item.created_at).format() >
                  moment(course.last_visited).format()
            )
          );
        }
        if (messages.length > 0) {
          // messagesData && setNotifications([...messagesData?.messages, ...messages]);
          messagesData &&
            setNewMessages(
              newMessagesArray.map((item) => {
                return {
                  classes: item.classes,
                  course: item.course,
                  faculty: item.faculty,
                };
              })
            );

          // messagesData && setUnReadNotifications(newMessagesArray.length);
        } else {
          if (newMessagesArray?.length > 0) {
            setNewMessages(
              newMessagesArray.map((item) => {
                return {
                  classes: item.classes,
                  course: item.course,
                  faculty: item.faculty,
                };
              })
            );
            // setUnReadNotifications(newMessagesArray.length);
          }
          // setNotifications(messagesData?.messages);
        }
      }
    }

    // eslint-disable-next-line
  }, [messagesData]);

  useEffect(() => {
    if (!messagesData) return;
    dispatch(setMessages(messagesData?.messages));
    // eslint-disable-next-line
  }, [messagesData]);

  useEffect(() => {
    if (id) dispatch(fetchCourses(id));
    // eslint-disable-next-line
  }, [id]);

  // useEffect(() => {
  //   if (messagesData?.messages?.length > 0) {
  //     // setNewMessages(true);
  //   }
  // }, [messagesData]);

  if (isLoading) {
    return <Loader />;
  }
  return (
    <>
      {toastMsg && <Toast />}
      <Navbar
        deferredPrompt={deferredPrompt}
        setDeferredPrompt={setDeferredPrompt}
        setMessages={setNewMessages}
        isBadgeActive={isBadgeActive}
        newMessages={newMessages.length > 0}
      />

      <Routes>
        <Route element={<RequireAuth user={user} />}>
          <Route path="/Home" element={<Home />} />
        </Route>
        <Route element={<RequireAuth user={user} />}>
          <Route
            path="/Messages"
            element={
              <Messages
                newMessages={newMessages}
                setNewMessages={setNewMessages}
              />
            }
          />
        </Route>
        <Route element={<RequireAuth user={user} />}>
          <Route
            path="/Messages/:course_code/:class_id/course-messages"
            element={<CourseMessages />}
          />
        </Route>
        {user?.role === "Faculty" && (
          <Route element={<RequireAuth user={user} />}>
            <Route
              path="/Messages/:course_code/course-sections"
              element={<CourseSections />}
            />
          </Route>
        )}
        {user?.role === "Faculty" && (
          <Route element={<RequireAuth user={user} />}>
            <Route
              path="/Messages/:course_code/create-message"
              element={<CreateMessage />}
            />
          </Route>
        )}

        <Route element={<RequireAuth user={user} />}>
          <Route
            path="/Notifications"
            element={
              <Notifications
                notifications={notifications}
                setIsBadgeActive={setIsBadgeActive}
                unReadNotifications={unReadNotifications}
                setUnReadNotifications={setUnReadNotifications}
                isBadgeActive={isBadgeActive}
              />
            }
          />
        </Route>
        <Route element={<RequireAuth user={user} />}>
          <Route path="/Week-Schedule" element={<WeekSchedule />} />
        </Route>
        <Route
          path="/login"
          element={
            <Login
              setUnReadNotifications={setUnReadNotifications}
              setIsBadgeActive={setIsBadgeActive}
              user={user}
              setNotifications={setNotifications}
            />
          }
        />
        <Route path="/reset-password/:id/:token" element={<ResetPassword />} />
        <Route
          path="*"
          element={
            !user ? (
              <Navigate replace to="/login" />
            ) : (
              <Navigate replace to="/Home" />
            )
          }
        />
      </Routes>
    </>
  );
}

export default App;
